import { TransactionType } from '../proto/generated/order'
import { QuoteType } from '../proto/generated/quick-trade'
import { OrderBook } from '../proto/generated/security'

/** Level 0 = top of book; +4 = 5th level on default side; −5 = 5th level on opposite side. */
export const LEVEL_QUOTE_MIN = -5
export const LEVEL_QUOTE_MAX = 4

export function quoteTypeLabel(type: QuoteType): string {
    switch (type) {
        case QuoteType.BIPS:
            return 'Bips'
        case QuoteType.TICKS:
            return 'Ticks'
        case QuoteType.LEVEL:
            return 'Level'
        default:
            return 'Bips'
    }
}

export function referenceSideLabel(transactionType: TransactionType): 'Ask' | 'Bid' {
    return transactionType === TransactionType.BUY ? 'Ask' : 'Bid'
}

function oppositeSideLabel(transactionType: TransactionType): 'Ask' | 'Bid' {
    return transactionType === TransactionType.BUY ? 'Bid' : 'Ask'
}

export type LevelBookLookup = { side: 'asks' | 'bids'; depthIndex: number }

/** Option B: 0 = default-side top; +N = deeper on default side; −N = opposite-side depth. */
export function resolveLevelBookLookup(
    transactionType: TransactionType,
    quoteValue: number
): LevelBookLookup | undefined {
    if (quoteValue < LEVEL_QUOTE_MIN || quoteValue > LEVEL_QUOTE_MAX) return undefined
    const isBuy = transactionType === TransactionType.BUY
    if (quoteValue >= 0) {
        return {
            side: isBuy ? 'asks' : 'bids',
            depthIndex: quoteValue,
        }
    }
    return {
        side: isBuy ? 'bids' : 'asks',
        depthIndex: Math.abs(quoteValue) - 1,
    }
}

export function resolveLevelPriceDisplay(
    transactionType: TransactionType,
    quoteValue: number,
    orderBook: OrderBook | undefined,
    topAsk: number,
    topBid: number,
    priceMultiplier: number
): number | undefined {
    const lookup = resolveLevelBookLookup(transactionType, quoteValue)
    if (!lookup) return undefined
    const pm = priceMultiplier > 0 ? priceMultiplier : 100
    const native = orderBook?.[lookup.side]?.[lookup.depthIndex]?.price ?? 0
    if (native > 0) return native / pm
    if (lookup.depthIndex === 0) {
        const scalar = lookup.side === 'asks' ? topAsk : topBid
        if (scalar > 0) return scalar / pm
    }
    return undefined
}

export function clampQuoteValue(quoteType: QuoteType, raw: number | string): number {
    const n = typeof raw === 'string' ? Number(raw) : raw
    if (!Number.isFinite(n)) return 0
    let value = Math.round(n)
    if (quoteType === QuoteType.LEVEL) {
        if (value > LEVEL_QUOTE_MAX) value = LEVEL_QUOTE_MAX
        else if (value < LEVEL_QUOTE_MIN) value = LEVEL_QUOTE_MIN
    }
    return value
}

export function formatSignedOffset(value: number, unit: string): string {
    if (value === 0) return ''
    const sign = value > 0 ? '+' : ''
    return `${sign}${value} ${unit}`
}

export function formatPriceOffsetLabel(
    transactionType: TransactionType,
    quoteType: QuoteType,
    quoteValue: number | undefined | null
): string {
    const value = quoteValue ?? 0
    if (quoteType === QuoteType.LEVEL) {
        if (value >= 0) {
            return `${referenceSideLabel(transactionType)} Level ${value + 1}`
        }
        return `${oppositeSideLabel(transactionType)} Level ${Math.abs(value)}`
    }

    const ref = referenceSideLabel(transactionType)
    if (value === 0) return ref

    const unit = quoteType === QuoteType.TICKS ? 'ticks' : 'bips'
    const offset = formatSignedOffset(value, unit)
    return `${ref} ${offset}`.trim()
}

export function formatLimitPriceDisplay(price: number | undefined): string {
    if (price == null || !Number.isFinite(price) || price <= 0) return '—'
    return parseFloat(price.toFixed(2)).toString()
}
